import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiClient, API_ENDPOINTS } from '../../config/api';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  CircularProgress,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  QuestionAnswer,
  Add,
  Edit,
  Delete,
  Visibility,
  PushPin,
  InfoOutlined,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';

// ─── Design tokens ─────────────────────────────────────────────────
const PINK  = '#EC4899';
const CORAL = '#FF6B35';

// ─── Types ─────────────────────────────────────────────────────────
interface FaqAdminListItem {
  id: number;
  categoryKey: string;
  categoryTitle: string;
  question: string;
  sortOrder: number;
  isPinned: boolean;
  viewCount: number;
  isActive: boolean;
  createdTime: string;
}

interface FaqAdminDetail extends FaqAdminListItem {
  answer: string;
  updatedTime?: string;
}

interface FaqFormData {
  category: string;
  question: string;
  answer: string;
  sortOrder: number;
  isPinned: boolean;
  isActive: boolean;
}

interface PageResponse<T> {
  content: T[];
  totalPages: number;
  totalElements: number;
  number: number;
  size: number;
}

// ─── Helpers ───────────────────────────────────────────────────────
const formatDateTime = (s: string) =>
  new Date(s).toLocaleString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

// ─── Category config ───────────────────────────────────────────────
const CATEGORY_CONFIG: Record<string, { label: string; color: string }> = {
  ACCOUNT:     { label: '계정',     color: '#1976D2' },
  PAYMENT:     { label: '결제',     color: '#E65100' },
  MATCHING:    { label: '매칭',     color: '#C2185B' },
  CHAT:        { label: '채팅',     color: '#2E7D32' },
  PRIVACY:     { label: '개인정보', color: '#6A1B9A' },
  REPORT:      { label: '신고',     color: '#BF360C' },
  RESTRICTION: { label: '제한',     color: '#37474F' },
};

const EMPTY_FORM: FaqFormData = {
  category: 'ACCOUNT',
  question: '',
  answer: '',
  sortOrder: 0,
  isPinned: false,
  isActive: true,
};

// ─── Sub-component: StatCard ────────────────────────────────────────
interface StatCardProps { icon: React.ReactNode; value: number; label: string; accent: string; }
const StatCard: React.FC<StatCardProps> = ({ icon, value, label, accent }) => (
  <Card elevation={0} sx={{
    border: '1px solid #F0E8F5', borderLeft: `4px solid ${accent}`, borderRadius: '14px',
    p: 2.5, display: 'flex', alignItems: 'center', gap: 2,
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' },
  }}>
    <Box sx={{ width: 48, height: 48, borderRadius: '12px', background: `${accent}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: accent, flexShrink: 0 }}>
      {icon}
    </Box>
    <Box>
      <Typography sx={{ fontSize: '1.8rem', fontWeight: 900, color: accent, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ fontSize: '0.75rem', color: '#888', fontWeight: 500, mt: 0.25 }}>{label}</Typography>
    </Box>
  </Card>
);

// ─── Main component ─────────────────────────────────────────────────
const AdminFaqPage: React.FC = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  const [faqs, setFaqs]                   = useState<FaqAdminListItem[]>([]);
  const [selected, setSelected]           = useState<FaqAdminDetail | null>(null);
  const [detailDialog, setDetailDialog]   = useState(false);
  const [formDialog, setFormDialog]       = useState(false);
  const [deleteDialog, setDeleteDialog]   = useState(false);
  const [isEditMode, setIsEditMode]       = useState(false);
  const [formData, setFormData]           = useState<FaqFormData>(EMPTY_FORM);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterActive, setFilterActive]   = useState('');
  const [page, setPage]                   = useState(0);
  const [totalPages, setTotalPages]       = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading]             = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [snack, setSnack]                 = useState<{ open: boolean; msg: string; severity: 'success' | 'error' }>({ open: false, msg: '', severity: 'success' });

  useEffect(() => {
    if (isLoading) return;
    if (!user || user.role !== 'ADMIN') navigate('/');
  }, [user, isLoading, navigate]);

  const showSnack = (msg: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, msg, severity });

  const fetchFaqs = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page: String(page), size: '20' });
      if (filterCategory) qs.append('category', filterCategory);
      if (filterActive !== '') qs.append('isActive', filterActive);
      const res = await apiClient.get<{ resultType: string; result: PageResponse<FaqAdminListItem> }>(
        `${API_ENDPOINTS.ADMIN.FAQS.LIST}?${qs.toString()}`
      );
      setFaqs(res.result.content);
      setTotalPages(res.result.totalPages);
      setTotalElements(res.result.totalElements);
    } catch {
      showSnack('목록 조회에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, filterCategory, filterActive]);

  useEffect(() => {
    if (!isLoading && user?.role === 'ADMIN') fetchFaqs();
  }, [fetchFaqs, isLoading, user]);

  const openDetail = async (item: FaqAdminListItem) => {
    setDetailLoading(true);
    setDetailDialog(true);
    try {
      const res = await apiClient.get<{ resultType: string; result: FaqAdminDetail }>(
        API_ENDPOINTS.ADMIN.FAQS.DETAIL(item.id)
      );
      setSelected(res.result);
    } catch {
      showSnack('상세 조회에 실패했습니다.', 'error');
      setDetailDialog(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const openCreate = () => {
    setIsEditMode(false);
    setFormData(EMPTY_FORM);
    setFormDialog(true);
  };

  const openEdit = async (item: FaqAdminListItem) => {
    setDetailLoading(true);
    try {
      const res = await apiClient.get<{ resultType: string; result: FaqAdminDetail }>(
        API_ENDPOINTS.ADMIN.FAQS.DETAIL(item.id)
      );
      setSelected(res.result);
      setFormData({
        category: res.result.categoryKey,
        question: res.result.question,
        answer: res.result.answer,
        sortOrder: res.result.sortOrder,
        isPinned: res.result.isPinned,
        isActive: res.result.isActive,
      });
      setIsEditMode(true);
      setFormDialog(true);
    } catch {
      showSnack('데이터 조회에 실패했습니다.', 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSubmitForm = async () => {
    if (!formData.question.trim() || !formData.answer.trim()) {
      showSnack('질문과 답변을 모두 입력해주세요.', 'error');
      return;
    }
    setActionLoading(true);
    try {
      if (isEditMode && selected) {
        await apiClient.patch(API_ENDPOINTS.ADMIN.FAQS.UPDATE(selected.id), {
          question: formData.question,
          answer: formData.answer,
          sortOrder: formData.sortOrder,
          isPinned: formData.isPinned,
          isActive: formData.isActive,
        });
        showSnack('FAQ가 수정되었습니다.');
      } else {
        await apiClient.post(API_ENDPOINTS.ADMIN.FAQS.CREATE, {
          category: formData.category,
          question: formData.question,
          answer: formData.answer,
          sortOrder: formData.sortOrder || undefined,
          isPinned: formData.isPinned,
        });
        showSnack('FAQ가 등록되었습니다.');
      }
      setFormDialog(false);
      fetchFaqs();
    } catch {
      showSnack(`FAQ ${isEditMode ? '수정' : '등록'}에 실패했습니다.`, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await apiClient.delete(API_ENDPOINTS.ADMIN.FAQS.DELETE(selected.id));
      showSnack('FAQ가 비활성화되었습니다.');
      setDeleteDialog(false);
      setDetailDialog(false);
      fetchFaqs();
    } catch {
      showSnack('삭제에 실패했습니다.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleQuickToggle = async (item: FaqAdminListItem, field: 'isPinned' | 'isActive') => {
    try {
      await apiClient.patch(API_ENDPOINTS.ADMIN.FAQS.UPDATE(item.id), { [field]: !item[field] });
      showSnack(field === 'isPinned' ? (item.isPinned ? '고정 해제되었습니다.' : '고정되었습니다.') : (item.isActive ? '비활성화되었습니다.' : '활성화되었습니다.'));
      fetchFaqs();
    } catch {
      showSnack('변경에 실패했습니다.', 'error');
    }
  };

  const stats = {
    total:   totalElements,
    active:  faqs.filter(f => f.isActive).length,
    pinned:  faqs.filter(f => f.isPinned).length,
    inactive: faqs.filter(f => !f.isActive).length,
  };

  if (isLoading || !user || user.role !== 'ADMIN') return null;

  return (
    <AdminLayout>
      <style>{`
        @keyframes rowIn { from{opacity:0;transform:translateX(-8px)} to{opacity:1;transform:translateX(0)} }
      `}</style>

      <Box sx={{ p: { xs: 2, md: 4 }, background: '#F8F4F9', minHeight: '100vh' }}>

        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="overline" sx={{ color: PINK, fontWeight: 700, letterSpacing: 3, fontSize: '0.7rem' }}>
              ADMIN · FAQ
            </Typography>
            <Typography variant="h4" sx={{
              fontWeight: 900, mt: 0.5,
              background: `linear-gradient(135deg, #1A0A2E 30%, ${CORAL})`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              FAQ 관리
            </Typography>
            <Typography sx={{ color: '#888', fontSize: '0.85rem', mt: 0.5 }}>
              자주 묻는 질문 등록 및 관리
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={openCreate}
            startIcon={<Add />}
            sx={{
              borderRadius: '12px', textTransform: 'none', fontWeight: 700, px: 3,
              background: `linear-gradient(135deg, ${PINK}, ${CORAL})`,
              boxShadow: `0 4px 14px ${PINK}44`,
              '&:hover': { background: `linear-gradient(135deg, #D1366E, #E55A20)` },
            }}
          >
            FAQ 등록
          </Button>
        </Box>

        {/* Stat cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 4 }}>
          <StatCard icon={<QuestionAnswer sx={{ fontSize: 24 }} />} value={stats.total}    label="전체 FAQ"   accent="#7B1FA2" />
          <StatCard icon={<CheckCircle sx={{ fontSize: 24 }} />}    value={stats.active}   label="활성 FAQ"   accent="#2E7D32" />
          <StatCard icon={<PushPin sx={{ fontSize: 24 }} />}        value={stats.pinned}   label="고정 FAQ"   accent={PINK} />
          <StatCard icon={<Cancel sx={{ fontSize: 24 }} />}         value={stats.inactive} label="비활성 FAQ" accent="#9E9E9E" />
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>카테고리</InputLabel>
            <Select value={filterCategory} label="카테고리" onChange={e => { setFilterCategory(e.target.value); setPage(0); }} sx={{ borderRadius: '10px' }}>
              <MenuItem value="">전체</MenuItem>
              {Object.entries(CATEGORY_CONFIG).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>활성 상태</InputLabel>
            <Select value={filterActive} label="활성 상태" onChange={e => { setFilterActive(e.target.value); setPage(0); }} sx={{ borderRadius: '10px' }}>
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="true">활성</MenuItem>
              <MenuItem value="false">비활성</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Table */}
        <Card elevation={0} sx={{ borderRadius: '20px', border: '1px solid #F0E8F5', overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: PINK }} /></Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(90deg, #1A0A2E 0%, #4A1265 100%)' }}>
                    {['ID', '카테고리', '질문', '정렬순서', '조회수', '고정', '상태', '처리'].map(h => (
                      <TableCell key={h} sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 700, fontSize: '0.75rem', py: 1.8, whiteSpace: 'nowrap' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {faqs.map((item, idx) => {
                    const catCfg = CATEGORY_CONFIG[item.categoryKey] ?? { label: item.categoryTitle, color: '#555' };
                    return (
                      <TableRow key={item.id} sx={{
                        animation: `rowIn 0.3s ease ${idx * 0.04}s both`,
                        borderLeft: item.isPinned ? `4px solid ${PINK}` : '4px solid transparent',
                        background: !item.isActive ? '#FAFAFA' : item.isPinned ? '#FFF5FB' : '#fff',
                        '&:hover': { background: item.isPinned ? '#FFEBF6' : '#FAF5FF' },
                        '& td': { borderBottom: '1px solid #F3EBF8' },
                        opacity: item.isActive ? 1 : 0.6,
                      }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <Typography sx={{ fontWeight: 700, color: '#555', fontSize: '0.85rem' }}>#{item.id}</Typography>
                            {item.isPinned && <PushPin sx={{ fontSize: 14, color: PINK }} />}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={catCfg.label} size="small"
                            sx={{ background: `${catCfg.color}15`, color: catCfg.color, border: `1px solid ${catCfg.color}30`, fontWeight: 700, fontSize: '0.7rem' }} />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 260 }}>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: item.isActive ? '#1A0A2E' : '#999' }}>
                            {item.question}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.85rem', color: '#666', textAlign: 'center' }}>{item.sortOrder}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.85rem', color: '#666' }}>{item.viewCount.toLocaleString()}</Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={item.isPinned ? '고정 해제' : '고정'}>
                            <IconButton size="small" onClick={() => handleQuickToggle(item, 'isPinned')}
                              sx={{ color: item.isPinned ? PINK : '#CCC', '&:hover': { background: '#FDE8F4' } }}>
                              <PushPin sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.6,
                            background: item.isActive ? '#E8F5E9' : '#F5F5F5',
                            color: item.isActive ? '#2E7D32' : '#9E9E9E',
                            border: `1px solid ${item.isActive ? '#A5D6A7' : '#E0E0E0'}`,
                            px: 1.2, py: 0.4, borderRadius: '100px', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap',
                          }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: item.isActive ? '#43A047' : '#BDBDBD' }} />
                            {item.isActive ? '활성' : '비활성'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title="상세 보기">
                              <IconButton size="small" onClick={() => openDetail(item)} sx={{ color: '#9C27B0', '&:hover': { background: '#F3E5F5' } }}>
                                <Visibility sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="수정">
                              <IconButton size="small" onClick={() => openEdit(item)} sx={{ color: '#1976D2', '&:hover': { background: '#E3F2FD' } }}>
                                <Edit sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            {item.isActive && (
                              <Tooltip title="비활성화">
                                <IconButton size="small" onClick={() => { setSelected(item as FaqAdminDetail); setDeleteDialog(true); }} sx={{ color: '#C62828', '&:hover': { background: '#FFEBEE' } }}>
                                  <Delete sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {faqs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <InfoOutlined sx={{ fontSize: 40, color: '#DDD', mb: 1 }} />
                        <Typography sx={{ color: '#AAA' }}>FAQ가 없습니다.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination count={totalPages} page={page + 1} onChange={(_, v) => setPage(v - 1)} color="primary" sx={{ '& .MuiPaginationItem-root': { borderRadius: '8px' } }} />
          </Box>
        )}

        {/* Detail Dialog */}
        <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: PINK }} /></Box>
          ) : selected && (() => {
            const catCfg = CATEGORY_CONFIG[selected.categoryKey] ?? { label: selected.categoryTitle, color: '#555' };
            return (
              <>
                <DialogTitle sx={{ p: 0 }}>
                  <Box sx={{ background: 'linear-gradient(135deg, #1A0A2E 0%, #4A1265 100%)', p: 3, color: '#fff' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: `${catCfg.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                        <QuestionAnswer />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>FAQ #{selected.id}</Typography>
                        <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)' }}>{formatDateTime(selected.createdTime)} 등록</Typography>
                      </Box>
                    </Box>
                  </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 3, pt: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip label={catCfg.label} size="small" sx={{ background: `${catCfg.color}15`, color: catCfg.color, fontWeight: 700 }} />
                      {selected.isPinned && <Chip icon={<PushPin sx={{ fontSize: '14px !important' }} />} label="고정" size="small" sx={{ background: '#FDE8F4', color: PINK, fontWeight: 700 }} />}
                      <Chip label={selected.isActive ? '활성' : '비활성'} size="small"
                        sx={{ background: selected.isActive ? '#E8F5E9' : '#F5F5F5', color: selected.isActive ? '#2E7D32' : '#9E9E9E', fontWeight: 700 }} />
                      <Chip label={`정렬 ${selected.sortOrder}`} size="small" sx={{ background: '#F3E5F5', color: '#7B1FA2', fontWeight: 700 }} />
                      <Chip label={`조회 ${selected.viewCount}`} size="small" sx={{ background: '#E3F2FD', color: '#1976D2', fontWeight: 700 }} />
                    </Box>
                    <Box sx={{ p: 2, borderRadius: '12px', background: '#F8F4F9', border: '1px solid #EDE0F5' }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600, mb: 1 }}>질문</Typography>
                      <Typography sx={{ fontWeight: 700, color: '#1A0A2E', fontSize: '0.95rem', lineHeight: 1.5 }}>{selected.question}</Typography>
                    </Box>
                    <Box sx={{ p: 2, borderRadius: '12px', background: '#E8F5E9', border: '1px solid #A5D6A7' }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#2E7D32', fontWeight: 700, mb: 1 }}>답변</Typography>
                      <Typography sx={{ fontSize: '0.9rem', color: '#333', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{selected.answer}</Typography>
                    </Box>
                  </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                  <Button variant="outlined" onClick={() => { setDetailDialog(false); openEdit(selected); }}
                    sx={{ borderRadius: '10px', borderColor: '#1976D2', color: '#1976D2', textTransform: 'none', fontWeight: 700 }}
                    startIcon={<Edit sx={{ fontSize: 16 }} />}>
                    수정
                  </Button>
                  {selected.isActive && (
                    <Button variant="outlined" onClick={() => setDeleteDialog(true)}
                      sx={{ borderRadius: '10px', borderColor: '#C62828', color: '#C62828', textTransform: 'none', fontWeight: 700 }}
                      startIcon={<Delete sx={{ fontSize: 16 }} />}>
                      비활성화
                    </Button>
                  )}
                  <Button onClick={() => setDetailDialog(false)} sx={{ borderRadius: '10px', textTransform: 'none', color: '#888' }}>닫기</Button>
                </DialogActions>
              </>
            );
          })()}
        </Dialog>

        {/* Form Dialog (Create / Edit) */}
        <Dialog open={formDialog} onClose={() => setFormDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}>
          <DialogTitle sx={{ p: 0 }}>
            <Box sx={{ background: `linear-gradient(135deg, ${PINK}, ${CORAL})`, p: 3, color: '#fff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {isEditMode ? <Edit /> : <Add />}
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>{isEditMode ? 'FAQ 수정' : 'FAQ 등록'}</Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3, pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <FormControl fullWidth size="small" disabled={isEditMode}>
                <InputLabel>카테고리</InputLabel>
                <Select value={formData.category} label="카테고리"
                  onChange={e => setFormData(f => ({ ...f, category: e.target.value }))}
                  sx={{ borderRadius: '12px' }}>
                  {Object.entries(CATEGORY_CONFIG).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth label="질문"
                value={formData.question}
                onChange={e => setFormData(f => ({ ...f, question: e.target.value }))}
                placeholder="자주 묻는 질문을 입력해주세요..."
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
              <TextField
                fullWidth multiline rows={6} label="답변"
                value={formData.answer}
                onChange={e => setFormData(f => ({ ...f, answer: e.target.value }))}
                placeholder="질문에 대한 자세한 답변을 작성해주세요..."
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  label="정렬 순서" type="number" size="small"
                  value={formData.sortOrder}
                  onChange={e => setFormData(f => ({ ...f, sortOrder: Number(e.target.value) }))}
                  sx={{ width: 120, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  inputProps={{ min: 0 }}
                />
                <FormControlLabel
                  control={<Switch checked={formData.isPinned} onChange={e => setFormData(f => ({ ...f, isPinned: e.target.checked }))} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: PINK }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: PINK } }} />}
                  label={<Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>상단 고정</Typography>}
                />
                {isEditMode && (
                  <FormControlLabel
                    control={<Switch checked={formData.isActive} onChange={e => setFormData(f => ({ ...f, isActive: e.target.checked }))} color="success" />}
                    label={<Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>활성화</Typography>}
                  />
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <Button onClick={() => setFormDialog(false)} sx={{ borderRadius: '10px', textTransform: 'none', color: '#888' }}>취소</Button>
            <Button
              variant="contained"
              disabled={actionLoading || !formData.question.trim() || !formData.answer.trim()}
              onClick={handleSubmitForm}
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3, background: `linear-gradient(135deg, ${PINK}, ${CORAL})`, '&:hover': { background: `linear-gradient(135deg, #D1366E, #E55A20)` } }}
              startIcon={actionLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : isEditMode ? <Edit /> : <Add />}
            >
              {isEditMode ? '수정 완료' : '등록'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirm Dialog */}
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}>
          <DialogTitle sx={{ p: 0 }}>
            <Box sx={{ background: 'linear-gradient(135deg, #B71C1C, #C62828)', p: 3, color: '#fff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Delete />
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>FAQ 비활성화</Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3, pt: 3 }}>
            <Box sx={{ p: 2, borderRadius: '12px', background: '#FFEBEE', border: '1px solid #EF9A9A' }}>
              <Typography sx={{ fontSize: '0.9rem', color: '#C62828', fontWeight: 600 }}>
                이 FAQ를 비활성화하시겠습니까?
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#888', mt: 1 }}>
                비활성화된 FAQ는 사용자에게 표시되지 않습니다.
              </Typography>
            </Box>
            {selected && (
              <Box sx={{ mt: 2, p: 1.5, borderRadius: '10px', background: '#F8F4F9', border: '1px solid #EDE0F5' }}>
                <Typography sx={{ fontSize: '0.8rem', color: '#666', fontWeight: 600 }}>대상: {selected.question}</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <Button onClick={() => setDeleteDialog(false)} sx={{ borderRadius: '10px', textTransform: 'none', color: '#888' }}>취소</Button>
            <Button variant="contained" disabled={actionLoading} onClick={handleDelete}
              sx={{ borderRadius: '10px', background: '#C62828', textTransform: 'none', fontWeight: 700, '&:hover': { background: '#B71C1C' } }}
              startIcon={actionLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <Delete />}>
              비활성화
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ borderRadius: '12px' }}>{snack.msg}</Alert>
        </Snackbar>
      </Box>
    </AdminLayout>
  );
};

export default AdminFaqPage;
